import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { useNavigate, Link } from 'react-router-dom';
import axiosInstance from 'config/axiosInstance';

const LiveWebinarsPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedInstructors, setSelectedInstructors] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sortBy, setSortBy] = useState('date');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showFilters, setShowFilters] = useState(false);
  const itemsPerPage = 12; // ✅ Show 12 cards per page

  const [totalCount, setTotalCount] = useState(0);
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  // ✅ Transform API data — defined BEFORE fetch
 const transformWebinarData = (apiWebinar) => {
  const utcDate = new Date(apiWebinar.scheduled_date);
  const istDate = new Date(utcDate.getTime() + 5.5 * 60 * 60 * 1000);

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = months[istDate.getUTCMonth()];
  const day = istDate.getUTCDate();
  const year = istDate.getUTCFullYear();

  const format12h = (date) => {
    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes().toString().padStart(2,'0');
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${period}`;
  };

  // Function to determine if a date is in daylight saving time for US timezones
  const isDaylightSavingTime = (date) => {
    const year = date.getUTCFullYear();
    // DST in US starts on second Sunday in March (2 AM) and ends on first Sunday in November (2 AM)
    const dstStart = new Date(Date.UTC(year, 2, 1));
    const dstEnd = new Date(Date.UTC(year, 10, 1));
    
    // Find second Sunday in March
    dstStart.setUTCDate(1);
    while (dstStart.getUTCDay() !== 0) { // 0 = Sunday
      dstStart.setUTCDate(dstStart.getUTCDate() + 1);
    }
    dstStart.setUTCDate(dstStart.getUTCDate() + 7); // Second Sunday
    
    // Find first Sunday in November
    dstEnd.setUTCDate(1);
    while (dstEnd.getUTCDay() !== 0) {
      dstEnd.setUTCDate(dstEnd.getUTCDate() + 1);
    }
    
    return date >= dstStart && date < dstEnd;
  };

  // Get appropriate timezone abbreviations
  const getPacificTimezone = (date) => {
    return isDaylightSavingTime(date) ? 'PDT' : 'PST';
  };

  const getEasternTimezone = (date) => {
    return isDaylightSavingTime(date) ? 'EDT' : 'EST';
  };

  // Calculate times
  const pstDate = istDate;
  const estDate = new Date(istDate.getTime() + 3 * 60 * 60 * 1000);

  const pstTime = format12h(pstDate);
  const estTime = format12h(estDate);
  
  const pacificTz = getPacificTimezone(pstDate);
  const easternTz = getEasternTimezone(estDate);
  
  const timeString = `${pstTime} ${pacificTz} | ${estTime} ${easternTz}`;

  return {
    id: apiWebinar.id,
    webinarId: apiWebinar.webinar_id,
    title: apiWebinar.title,
    date: day.toString(),
    month: month.toUpperCase(),
    instructor: {
      name: apiWebinar.speaker?.full_name || apiWebinar.speaker?.name || 'Instructor',
      avatar: apiWebinar.speaker?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face&auto=format&q=60'
    },
    image: apiWebinar.cover_image_url || 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=500&h=300&fit=crop&auto=format&q=80',
    rating: apiWebinar.analytics?.average_rating || 4.8,
    reviewCount: apiWebinar.analytics?.total_reviews
      ? `${(apiWebinar.analytics.total_reviews / 1000).toFixed(1)}k`
      : '0',
    price: `$${parseFloat(apiWebinar.main_price).toFixed(2)}`,
    originalPrice: apiWebinar.original_price
      ? `$${parseFloat(apiWebinar.original_price).toFixed(2)}`
      : null,
    duration: apiWebinar.duration || 'N/A',
    level: apiWebinar.skill_level || 'All Levels',
    students: apiWebinar.total_students
      ? `${(apiWebinar.total_students / 1000).toFixed(1)}k`
      : '0',
    category: apiWebinar.category?.name || 'General',
    fullDate: `${month} ${day}, ${year}`,
    timeRange: timeString,
    scheduled_date: apiWebinar.scheduled_date
  };
};

  // 🔁 FETCH ALL WEBINARS PROGRESSIVELY
  useEffect(() => {
    const fetchWebinars = async () => {
      try {
        setLoading(true);
        setError(null);
        setWebinars([]);
        setProgress(0);
        setTotalCount(0);

        let nextUrl = `webinars/?webinar_type=live&status=scheduled&page_size=50`;
        let totalCountLocal = 0;

        while (nextUrl) {
          const response = await axiosInstance.get(nextUrl);
          
          if (totalCountLocal === 0) {
            totalCountLocal = response.data.count || response.data.results?.length || 100;
            setTotalCount(totalCountLocal);
          }
          
          const webinarsPage = response.data.results 
            ? response.data.results.map(transformWebinarData)
            : response.data.map(transformWebinarData);
          
          setWebinars(prev => {
            const updated = [...prev, ...webinarsPage].sort(
              (a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date)
            );
            const progress = totalCountLocal > 0 
              ? Math.min(100, (updated.length / totalCountLocal) * 100)
              : 0;
            setProgress(progress);
            return updated;
          });
          
          nextUrl = response.data.next;
          
          // ✅ Faster loading (was 300ms)
          if (nextUrl) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      } catch (err) {
        console.error('❌ Error fetching webinars:', err);
        setError('Failed to load webinars. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchWebinars();
  }, []); // ✅ Empty deps if axiosInstance is stable

  // 🔍 Filter & Sort
  const filterOptions = useMemo(() => ({
    months: [...new Set(webinars.map(w => new Date(w.scheduled_date).toLocaleString('default', { month: 'long' })))],
    instructors: [...new Set(webinars.map(w => w.instructor.name))],
    categories: [...new Set(webinars.map(w => w.category))]
  }), [webinars]);

  const filteredWebinars = useMemo(() => {
    let filtered = webinars.filter(w => {
      const matchesSearch = !searchQuery || 
        w.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        w.instructor.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesMonth = !selectedMonths.length || selectedMonths.includes(new Date(w.scheduled_date).toLocaleString('default', { month: 'long' }));
      const matchesInstructor = !selectedInstructors.length || selectedInstructors.includes(w.instructor.name);
      const matchesCategory = !selectedCategories.length || selectedCategories.includes(w.category);
      return matchesSearch && matchesMonth && matchesInstructor && matchesCategory;
    });

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date': return new Date(a.scheduled_date) - new Date(b.scheduled_date);
        case 'price-low': return parseFloat(a.price.slice(1)) - parseFloat(b.price.slice(1));
        case 'price-high': return parseFloat(b.price.slice(1)) - parseFloat(a.price.slice(1));
        default: return 0;
      }
    });
  }, [webinars, searchQuery, selectedMonths, selectedInstructors, selectedCategories, sortBy]);

  // 📄 Pagination: 12 per page
  const totalPages = Math.ceil(filteredWebinars.length / itemsPerPage);
  const paginatedWebinars = filteredWebinars.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const generateSlug = (title) => 
    title.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').trim();

  // 🌀 Loading State
  if (loading && webinars.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"
          />
          <p className="text-xl font-semibold text-blue-900">Loading webinars...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-cyan-50">
      {/* Hero Header */}
      <main className="pt-14 md:pt-16 lg:pt-20">
        <div className="relative bg-gradient-to-r from-blue-900 via-indigo-900 to-blue-900 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white rounded-full"
                animate={{
                  y: [0, -30, 0],
                  opacity: [0.2, 0.8, 0.2]
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2
                }}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`
                }}
              />
            ))}
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-5xl md:text-6xl font-black mb-4">
                Live <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Webinars</span>
              </h1>

              {/* Search Bar */}
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <Icon name="Search" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by title or instructor..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white text-gray-900 placeholder-gray-400 focus:ring-4 focus:ring-blue-500/50 focus:outline-none shadow-2xl"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filters Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-6 py-3 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all font-semibold text-gray-900"
              >
                <Icon name="SlidersHorizontal" size={18} />
                Filters
                {(selectedMonths.length + selectedInstructors.length + selectedCategories.length > 0) && (
                  <span className="bg-blue-600 text-white px-2 py-0.5 rounded-full text-xs">
                    {selectedMonths.length + selectedInstructors.length + selectedCategories.length}
                  </span>
                )}
              </button>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 bg-white rounded-xl shadow-lg border-none focus:ring-2 focus:ring-blue-500 font-medium"
              >
                <option value="date">📅 By Date</option>
                <option value="price-low">💰 Price: Low to High</option>
                <option value="price-high">💎 Price: High to Low</option>
              </select>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex bg-white rounded-xl p-1 shadow-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  <Icon name="Grid" size={18} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  <Icon name="List" size={18} />
                </button>
              </div>

              <div className="text-sm font-semibold text-gray-700 bg-white px-4 py-3 rounded-xl shadow-lg">
                {filteredWebinars.length} Results
              </div>
            </div>
          </div>

          {/* Expandable Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="bg-white rounded-2xl shadow-xl mb-8 overflow-hidden"
              >
                <div className="p-6 grid md:grid-cols-3 gap-6">
                  {/* Months */}
                  <div>
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Icon name="Calendar" size={18} className="text-blue-600" />
                      Month
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {filterOptions.months.map((month) => (
                        <label key={month} className="flex items-center gap-2 cursor-pointer hover:bg-blue-50 p-2 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedMonths.includes(month)}
                            onChange={() => setSelectedMonths(prev => 
                              prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
                            )}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span className="text-sm text-gray-700">{month}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Instructors */}
                  <div>
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Icon name="Users" size={18} className="text-indigo-600" />
                      Instructor
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {filterOptions.instructors.map((instructor) => (
                        <label key={instructor} className="flex items-center gap-2 cursor-pointer hover:bg-indigo-50 p-2 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedInstructors.includes(instructor)}
                            onChange={() => setSelectedInstructors(prev => 
                              prev.includes(instructor) ? prev.filter(i => i !== instructor) : [...prev, instructor]
                            )}
                            className="w-4 h-4 text-indigo-600 rounded"
                          />
                          <span className="text-sm text-gray-700">{instructor}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Categories */}
                  <div>
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Icon name="Folder" size={18} className="text-cyan-600" />
                      Category
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {filterOptions.categories.map((category) => (
                        <label key={category} className="flex items-center gap-2 cursor-pointer hover:bg-cyan-50 p-2 rounded-lg transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedCategories.includes(category)}
                            onChange={() => setSelectedCategories(prev => 
                              prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
                            )}
                            className="w-4 h-4 text-cyan-600 rounded"
                          />
                          <span className="text-sm text-gray-700">{category}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Webinars Grid/List — MAX 12 PER PAGE */}
          {paginatedWebinars.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
              <Icon name="Search" size={64} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No webinars found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search or filters</p>
              <Button onClick={() => {
                setSearchQuery('');
                setSelectedMonths([]);
                setSelectedInstructors([]);
                setSelectedCategories([]);
              }}>
                Clear All Filters
              </Button>
            </div>
          ) : (
            <>
              <div className={viewMode === 'grid' 
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" 
                : "space-y-4"
              }>
                {paginatedWebinars.map((webinar, index) => {
                  const slug = generateSlug(webinar.title);
                  const webinarUrl = `/live-webinar/${webinar.webinarId}/${slug}`;

                  return viewMode === 'grid' ? (
                    // Grid Card View
                    <motion.div
                      key={webinar.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ y: -8, scale: 1.02 }}
                      className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all overflow-hidden group"
                    >
                      <Link to={webinarUrl} className="block">
                        {/* Image */}
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={webinar.image}
                            alt={webinar.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          
                          {/* Live Badge */}
                          <div className="absolute top-3 left-3">
                            <div className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5 shadow-lg">
                              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                              LIVE
                            </div>
                          </div>

                          {/* Date Badge */}
                          <div className="absolute top-3 right-3">
                            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 via-indigo-500 to-blue-600 p-1 shadow-2xl">
                              <div className="w-full h-full bg-white rounded-full flex flex-col items-center justify-center">
                                <div className="text-2xl font-black bg-gradient-to-br from-blue-600 to-indigo-600 bg-clip-text text-transparent leading-none">
                                  {webinar.date}
                                </div>
                                <div className="text-[10px] font-bold text-indigo-600 uppercase mt-0.5">
                                  {webinar.month}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          <h3 className="font-bold text-gray-900 mb-3 line-clamp-2 text-base group-hover:text-blue-600 transition-colors">
                            {webinar.title}
                          </h3>

                          {/* Instructor */}
                          <div className="flex items-center gap-2 mb-3">
                            <img
                              src={webinar.instructor.avatar}
                              alt={webinar.instructor.name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                            <span className="text-sm text-gray-600 font-medium">{webinar.instructor.name}</span>
                          </div>

                          {/* Meta */}
                          <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
                            <div className="flex items-center gap-1">
                              <Icon name="Clock" size={12} />
                              {webinar.duration} min
                            </div>
                            <div className="flex items-center gap-1">
                              <Icon name="Star" size={12} className="text-yellow-400 fill-current" />
                              {webinar.rating}
                            </div>
                          </div>

                          {/* Date & Time */}
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-3 mb-4 border border-blue-100">
                            <div className="text-xs font-bold text-blue-900 mb-1">{webinar.fullDate}</div>
                            <div className="text-xs text-blue-700">{webinar.timeRange}</div>
                          </div>

                          {/* Price & CTA */}
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-2xl font-black text-blue-600">{webinar.price}</div>
                              {webinar.originalPrice && (
                                <div className="text-xs text-gray-400 line-through">{webinar.originalPrice}</div>
                              )}
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold px-6 py-2.5 rounded-xl hover:shadow-lg transition-all flex items-center gap-2"
                            >
                              Enroll Now
                              <Icon name="ArrowRight" size={14} />
                            </motion.button>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ) : (
                    // List View
                    <motion.div
                      key={webinar.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      whileHover={{ scale: 1.01 }}
                      className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all overflow-hidden"
                    >
                      <Link to={webinarUrl} className="flex flex-col md:flex-row">
                        {/* Image */}
                        <div className="relative md:w-64 h-48 md:h-auto flex-shrink-0">
                          <img
                            src={webinar.image}
                            alt={webinar.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-3 left-3">
                            <div className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5">
                              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                              LIVE
                            </div>
                          </div>
                          <div className="absolute top-3 right-3">
                            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 via-indigo-500 to-blue-600 p-1 shadow-2xl">
                              <div className="w-full h-full bg-white rounded-full flex flex-col items-center justify-center">
                                <div className="text-2xl font-black bg-gradient-to-br from-blue-600 to-indigo-600 bg-clip-text text-transparent leading-none">
                                  {webinar.date}
                                </div>
                                <div className="text-[10px] font-bold text-indigo-600 uppercase mt-0.5">
                                  {webinar.month}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 p-6 flex flex-col justify-between">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-blue-600 transition-colors">
                              {webinar.title}
                            </h3>
                            <div className="flex items-center gap-3 mb-4">
                              <img
                                src={webinar.instructor.avatar}
                                alt={webinar.instructor.name}
                                className="w-10 h-10 rounded-full"
                              />
                              <div>
                                <div className="text-sm font-semibold text-gray-900">{webinar.instructor.name}</div>
                                <div className="text-xs text-gray-500">{webinar.category}</div>
                              </div>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-3 inline-block mb-4">
                              <div className="text-sm font-bold text-blue-900">{webinar.fullDate}</div>
                              <div className="text-xs text-blue-700">{webinar.timeRange}</div>
                            </div>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="text-3xl font-black text-blue-600">{webinar.price}</div>
                              <div className="flex items-center gap-3 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <Icon name="Clock" size={14} />
                                  {webinar.duration} min
                                </span>
                                <span className="flex items-center gap-1">
                                  <Icon name="Star" size={14} className="text-yellow-400 fill-current" />
                                  {webinar.rating}
                                </span>
                              </div>
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold px-8 py-3 rounded-xl hover:shadow-lg transition-all flex items-center gap-2"
                            >
                              Enroll Now
                              <Icon name="ArrowRight" size={16} />
                            </motion.button>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>

              {/* Pagination */}
           {totalPages > 1 && (
  <div className="flex flex-col items-center space-y-4 mt-12 pt-8">
    {/* Progress bar */}
    <div className="w-64 h-1 bg-[#d6e6f7] rounded-full overflow-hidden">
      <div 
        className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transition-all duration-300"
        style={{ width: `${(currentPage / totalPages) * 100}%` }}
      />
    </div>
    
    <div className="flex items-center justify-between w-full max-w-md">
      <Button
        variant="outline"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
        className="text-[#093389] border-[#d6e6f7] hover:bg-[#f5f9ff]"
      >
        <Icon name="ChevronLeft" size={16} className="mr-1" />
        
      </Button>
      
     <div className="flex items-center space-x-2">
  <span className="text-sm text-[#555555]">Page</span>
  <div className="relative">
    <select
      value={currentPage}
      onChange={(e) => setCurrentPage(parseInt(e.target.value))}
      className="px-3 py-2 border border-[#d6e6f7] rounded-lg text-[#093389] font-medium focus:ring-2 focus:ring-[#0078d4] focus:border-transparent bg-white appearance-none pr-8"
      style={{
        WebkitAppearance: 'none',
        MozAppearance: 'none'
      }}
    >
      {Array.from({ length: totalPages }, (_, i) => (
        <option key={i + 1} value={i + 1}>
          {i + 1}
        </option>
      ))}
    </select>
    
  </div>
  <span className="text-sm text-[#555555]">of {totalPages}</span>
</div>
      <Button
        variant="outline"
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(currentPage + 1)}
        className="text-[#093389] border-[#d6e6f7] hover:bg-[#f5f9ff]"
      >
        
        <Icon name="ChevronRight" size={16} className="ml-1" />
      </Button>
    </div>
    
    {/* Quick page indicators */}
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((num) => {
        const pageNum = Math.min(
          Math.max(1, currentPage - 2 + num),
          totalPages
        );
        
        if (num === 1 && pageNum > 1) {
          return (
            <button
              key="first"
              onClick={() => setCurrentPage(1)}
              className="w-8 h-8 rounded-lg text-xs font-medium bg-white border border-[#d6e6f7] text-[#093389] hover:bg-[#f5f9ff]"
            >
              1
            </button>
          );
        }
        
        if (num === 5 && pageNum < totalPages) {
          return (
            <button
              key="last"
              onClick={() => setCurrentPage(totalPages)}
              className="w-8 h-8 rounded-lg text-xs font-medium bg-white border border-[#d6e6f7] text-[#093389] hover:bg-[#f5f9ff]"
            >
              {totalPages}
            </button>
          );
        }
        
        if (pageNum > 1 && pageNum < totalPages) {
          return (
            <button
              key={pageNum}
              onClick={() => setCurrentPage(pageNum)}
              className={`w-8 h-8 rounded-lg text-xs font-medium transition-all ${
                currentPage === pageNum
                  ? 'bg-gradient-to-r from-[#0078d4] to-[#064ad4] text-white shadow-md'
                  : 'bg-white border border-[#d6e6f7] text-[#093389] hover:bg-[#f5f9ff]'
              }`}
            >
              {pageNum}
            </button>
          );
        }
        
        return null;
      })}
    </div>
  </div>
)}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default LiveWebinarsPage;